import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Loader2, Upload, Printer, Search } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

/**
 * Toolbar with search + class filter + CSV import + print codes + add-student
 * buttons for /admin/students. Purely presentational — parent owns state.
 */
export const StudentToolbar = ({
  searchQuery, onSearchChange,
  classes,
  selectedClass, onSelectedClassChange,
  onOpenCsv,
  onPrintCodes, loadingPrint,
  onOpenCreate,
}) => {
  const { t } = useLanguage();
  const noClasses = classes.length === 0;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={`${t('common.search')}...`}
          className="pl-9 w-[180px] bg-card border-border"
          data-testid="student-search-input"
        />
      </div>

      {classes.length > 0 && (
        <Select value={selectedClass} onValueChange={onSelectedClassChange}>
          <SelectTrigger className="w-[170px] bg-card border-border" data-testid="student-class-filter">
            <SelectValue placeholder={t('students.selectClass')} />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {classes.map((cls) => (
              <SelectItem key={cls.class_id} value={cls.class_id}>
                {cls.name} ({cls.year})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Button
        onClick={onOpenCsv}
        variant="outline"
        disabled={noClasses}
        data-testid="csv-import-btn"
      >
        <Upload className="w-4 h-4 mr-2" />
        {t('students.importCsv')}
      </Button>

      <Button
        onClick={onPrintCodes}
        variant="outline"
        disabled={noClasses || loadingPrint}
        data-testid="print-codes-btn"
      >
        {loadingPrint ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
        {t('students.printCodes')}
      </Button>

      <Button
        onClick={onOpenCreate}
        className="bg-primary hover:bg-primary/90"
        disabled={noClasses}
        data-testid="add-student-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        {t('students.addStudent')}
      </Button>
    </div>
  );
};
